<script setup>
import { ref, reactive, computed, onUnmounted } from 'vue';
import { useElementSize, useLocalStorage } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { MIN_ZOOM, MAX_ZOOM } from '../../composables/useCsmlFlowGraph';
import {
  GRID,
  NODE_MIN_HEIGHT,
  NODE_WIDTH,
  snapToGrid,
} from '../../constants/csmlLayout';
import CsmlMinimap from './CsmlMinimap.vue';
import CsmlCanvasControls from './CsmlCanvasControls.vue';
import CsmlFlowNode from './CsmlFlowNode.vue';
import CsmlConnectionsLayer from './CsmlConnectionsLayer.vue';

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  connections: { type: Array, default: () => [] },
  selectedNodeIds: { type: Array, default: () => [] },
  selectedConnection: { type: Object, default: null },
  zoom: { type: Number, default: 1 },
  isPanMode: { type: Boolean, default: false },
  offsetX: { type: Number, default: 0 },
  offsetY: { type: Number, default: 0 },
  guides: { type: Object, default: null },
  wireStyle: { type: String, default: 'bezier' },
});

const emit = defineEmits([
  'dropNode',
  'selectNode',
  'selectNodes',
  'selectConnection',
  'removeConnection',
  'clearSelection',
  'nodeDrag',
  'nodeDragEnd',
  'deleteNode',
  'duplicateNode',
  'addNext',
  'addAtPort',
  'portClick',
  'update:offsetX',
  'update:offsetY',
  'update:zoom',
  'update:wireStyle',
  'tidy',
]);

const { t } = useI18n();
const ZOOM_STEP = 0.1;
const canvasRef = ref(null);
const tempConnection = ref(null);
const connecting = ref(null);
const portOffsetsByNode = reactive({});

const connectingFromId = computed(() => connecting.value?.fromId ?? null);

// Which out-ports already drive a connection, so a node can dim the dots of
// the branches that still lead nowhere.
const connectedPortsByNode = computed(() => {
  const map = {};
  props.connections.forEach(conn => {
    const list = map[conn.fromId] || (map[conn.fromId] = []);
    list.push(conn.fromPortIdx ?? 0);
  });
  return map;
});

const incomingNodeIds = computed(
  () => new Set(props.connections.map(conn => conn.toId))
);

const nodeHeights = reactive({});
const { width: viewportWidth, height: viewportHeight } =
  useElementSize(canvasRef);
const minimapOpen = useLocalStorage('csml-builder-minimap-open', true);
// The minimap sits right of the palette, which is wider when expanded.
const paletteCollapsed = useLocalStorage(
  'csml-builder-palette-collapsed',
  false
);

const onPortsMeasured = ({ nodeId, height, in: inPorts, out: outPorts }) => {
  portOffsetsByNode[nodeId] = { in: inPorts, out: outPorts };
  nodeHeights[nodeId] = height;
};

// The dot grid is painted on viewport-sized layers and slid/scaled with the
// pan and zoom, so it never runs out however far the flow is dragged. A faint
// dot every GRID px plus a stronger one every MAJOR_EVERY cells gives the eye
// something to count by; the faint tier fades out when zoomed far out.
const MAJOR_EVERY = 5;
const MINOR_FADE_ZOOM = 0.6;

const layerStyle = cell => ({
  backgroundPosition: `${props.offsetX}px ${props.offsetY}px`,
  backgroundSize: `${cell * props.zoom}px ${cell * props.zoom}px`,
});
const minorGridStyle = computed(() => ({
  ...layerStyle(GRID),
  opacity: props.zoom < MINOR_FADE_ZOOM ? 0 : 1,
}));
const majorGridStyle = computed(() => layerStyle(GRID * MAJOR_EVERY));

const transformStyle = computed(() => ({
  transform: `translate(${props.offsetX}px, ${props.offsetY}px) scale(${props.zoom})`,
  transformOrigin: '0 0',
}));

// While drawing a connection, keep panning the canvas toward the cursor as
// long as it rests near the viewport edge - otherwise a target port sitting
// off-screen (very easy to end up with on a large flow, since the canvas
// clips anything past its own edge) is simply unreachable mid-connection:
// there's no way to pan without first cancelling the connection you're in
// the middle of drawing. Runs on rAF rather than only reacting to mousemove
// so it keeps scrolling even while the cursor sits still against the edge.
const AUTO_PAN_EDGE_MARGIN = 32;
const AUTO_PAN_STEP = 12;
let lastPointer = null;
let autoPanFrame = null;

const autoPanTick = () => {
  autoPanFrame = null;
  if (!tempConnection.value || !canvasRef.value || !lastPointer) return;
  const rect = canvasRef.value.getBoundingClientRect();

  let panDx = 0;
  let panDy = 0;
  if (lastPointer.x < rect.left + AUTO_PAN_EDGE_MARGIN) panDx = AUTO_PAN_STEP;
  else if (lastPointer.x > rect.right - AUTO_PAN_EDGE_MARGIN) {
    panDx = -AUTO_PAN_STEP;
  }
  if (lastPointer.y < rect.top + AUTO_PAN_EDGE_MARGIN) panDy = AUTO_PAN_STEP;
  else if (lastPointer.y > rect.bottom - AUTO_PAN_EDGE_MARGIN) {
    panDy = -AUTO_PAN_STEP;
  }
  if (!panDx && !panDy) return;

  const nextOffsetX = props.offsetX + panDx;
  const nextOffsetY = props.offsetY + panDy;
  emit('update:offsetX', nextOffsetX);
  emit('update:offsetY', nextOffsetY);
  tempConnection.value = {
    ...tempConnection.value,
    x: (lastPointer.x - rect.left - nextOffsetX) / props.zoom,
    y: (lastPointer.y - rect.top - nextOffsetY) / props.zoom,
  };
  autoPanFrame = requestAnimationFrame(autoPanTick);
};

const onTempMove = e => {
  if (!tempConnection.value || !canvasRef.value) return;
  lastPointer = { x: e.clientX, y: e.clientY };
  const rect = canvasRef.value.getBoundingClientRect();
  tempConnection.value = {
    ...tempConnection.value,
    x: (e.clientX - rect.left - props.offsetX) / props.zoom,
    y: (e.clientY - rect.top - props.offsetY) / props.zoom,
  };
  if (!autoPanFrame) autoPanFrame = requestAnimationFrame(autoPanTick);
};

const DRAG_CONNECT_MIN = 6;
let connectOrigin = null;
let activeConnectUp = null;

const clearConnecting = () => {
  if (activeConnectUp) window.removeEventListener('mouseup', activeConnectUp);
  activeConnectUp = null;
  connectOrigin = null;
  connecting.value = null;
  tempConnection.value = null;
  lastPointer = null;
  if (autoPanFrame) {
    cancelAnimationFrame(autoPanFrame);
    autoPanFrame = null;
  }
  window.removeEventListener('mousemove', onTempMove);
};

// Pressing an out-port and dragging draws the wire live; releasing over any
// card finishes it. A press-and-release without moving falls back to the
// older click-then-click flow, so both gestures work.
const onConnectMouseUp = e => {
  activeConnectUp = null;
  if (!connecting.value || !connectOrigin) return;
  const dragged =
    Math.hypot(e.clientX - connectOrigin.x, e.clientY - connectOrigin.y) >
    DRAG_CONNECT_MIN;
  if (!dragged) return;
  const nodeId = document
    .elementFromPoint(e.clientX, e.clientY)
    ?.closest('[data-node-root]')?.dataset.nodeId;
  const target = props.nodes.find(node => String(node.id) === nodeId);
  if (target && target.id !== connecting.value.fromId) {
    emit('portClick', {
      fromId: connecting.value.fromId,
      toId: target.id,
      fromPortIdx: connecting.value.fromPortIdx,
    });
  }
  clearConnecting();
};

const onDrop = event => {
  event.preventDefault();
  const type = event.dataTransfer.getData('text/plain');
  if (!type || !canvasRef.value) return;
  const rect = canvasRef.value.getBoundingClientRect();
  const x = (event.clientX - rect.left - props.offsetX) / props.zoom;
  const y = (event.clientY - rect.top - props.offsetY) / props.zoom;
  // Centre the new card on the cursor instead of hanging it off the corner.
  emit('dropNode', {
    type,
    x: snapToGrid(x - NODE_WIDTH / 2),
    y: snapToGrid(y - NODE_MIN_HEIGHT / 2),
  });
};

// A marquee drag ends in a mouseup over the canvas, which the browser also
// reports as a click - that click must not wipe the selection just made.
let suppressNextClick = false;

const onCanvasBackgroundClick = event => {
  if (suppressNextClick) {
    suppressNextClick = false;
    return;
  }
  if (
    event.target.closest('[data-node-root]') ||
    event.target.closest('[data-port]') ||
    event.target.closest('svg')
  ) {
    return;
  }
  emit('clearSelection');
  if (connecting.value) clearConnecting();
};

let panStart = null;
let activePanMove = null;
let activePanUp = null;

const stopPanDrag = () => {
  panStart = null;
  if (activePanMove) window.removeEventListener('mousemove', activePanMove);
  if (activePanUp) window.removeEventListener('mouseup', activePanUp);
  activePanMove = null;
  activePanUp = null;
};

const marquee = ref(null);
let marqueeStart = null;
let activeMarqueeMove = null;
let activeMarqueeUp = null;

const stopMarquee = () => {
  marqueeStart = null;
  marquee.value = null;
  if (activeMarqueeMove) {
    window.removeEventListener('mousemove', activeMarqueeMove);
  }
  if (activeMarqueeUp) window.removeEventListener('mouseup', activeMarqueeUp);
  activeMarqueeMove = null;
  activeMarqueeUp = null;
};

const marqueeRect = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  width: Math.abs(a.x - b.x),
  height: Math.abs(a.y - b.y),
});

const MARQUEE_MIN_DRAG = 4;

// Dragging on empty canvas rubber-bands a box; every card it touches joins
// the selection (Shift keeps whatever was already selected).
const startMarquee = event => {
  const rect = canvasRef.value.getBoundingClientRect();
  const toLocal = e => ({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  marqueeStart = toLocal(event);
  const additive = event.shiftKey;

  activeMarqueeMove = e => {
    if (!marqueeStart) return;
    marquee.value = marqueeRect(marqueeStart, toLocal(e));
  };
  activeMarqueeUp = e => {
    const box = marqueeRect(marqueeStart, toLocal(e));
    stopMarquee();
    if (box.width < MARQUEE_MIN_DRAG && box.height < MARQUEE_MIN_DRAG) return;
    suppressNextClick = true;
    const left = (box.x - props.offsetX) / props.zoom;
    const top = (box.y - props.offsetY) / props.zoom;
    const right = left + box.width / props.zoom;
    const bottom = top + box.height / props.zoom;
    const hits = props.nodes
      .filter(node => {
        const height = nodeHeights[node.id] || NODE_MIN_HEIGHT;
        return (
          node.x < right &&
          node.x + NODE_WIDTH > left &&
          node.y < bottom &&
          node.y + height > top
        );
      })
      .map(node => node.id);
    emit('selectNodes', { ids: hits, additive });
  };
  window.addEventListener('mousemove', activeMarqueeMove);
  window.addEventListener('mouseup', activeMarqueeUp);
};

const onPanDown = event => {
  if (
    !props.isPanMode &&
    event.button === 0 &&
    event.target === event.currentTarget
  ) {
    startMarquee(event);
    return;
  }
  if (!props.isPanMode && event.button !== 1) return;
  if (!props.isPanMode && event.target !== event.currentTarget) return;
  if (props.isPanMode || event.button === 1) {
    panStart = {
      x: event.clientX,
      y: event.clientY,
      ox: props.offsetX,
      oy: props.offsetY,
    };
    activePanMove = e => {
      if (!panStart) return;
      emit('update:offsetX', panStart.ox + (e.clientX - panStart.x));
      emit('update:offsetY', panStart.oy + (e.clientY - panStart.y));
    };
    activePanUp = () => stopPanDrag();
    window.addEventListener('mousemove', activePanMove);
    window.addEventListener('mouseup', activePanUp);
  }
};

const handlePortClick = ({ nodeId, side, portIdx, clientX, clientY }) => {
  if (side === 'out') {
    // Second click on an out port cancels, matching builder4.html
    if (connecting.value && connecting.value.fromId === nodeId) {
      clearConnecting();
      return;
    }
    connecting.value = { fromId: nodeId, fromPortIdx: portIdx };
    const fromNode = props.nodes.find(n => n.id === nodeId);
    const outPort = portOffsetsByNode[nodeId]?.out?.find(
      p => p.idx === portIdx
    );
    tempConnection.value = {
      fromId: nodeId,
      fromPortIdx: portIdx,
      x: (fromNode?.x || 0) + (outPort?.x ?? NODE_WIDTH),
      y: (fromNode?.y || 0) + (outPort?.y ?? NODE_MIN_HEIGHT / 2),
    };
    window.removeEventListener('mousemove', onTempMove);
    window.addEventListener('mousemove', onTempMove);
    connectOrigin = { x: clientX, y: clientY };
    activeConnectUp = onConnectMouseUp;
    window.addEventListener('mouseup', onConnectMouseUp, { once: true });
    return;
  }

  if (side === 'in') {
    if (!connecting.value) {
      return;
    }
    if (connecting.value.fromId === nodeId) {
      clearConnecting();
      return;
    }
    emit('portClick', {
      fromId: connecting.value.fromId,
      toId: nodeId,
      fromPortIdx: connecting.value.fromPortIdx,
    });
    clearConnecting();
  }
};

onUnmounted(() => {
  clearConnecting();
  stopPanDrag();
  stopMarquee();
});

const clampZoom = value => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

// Zooms while keeping the content under (clientX, clientY) visually fixed,
// so the flow doesn't jump/drift when the user zooms in or out.
const zoomAtPoint = (nextZoom, clientX, clientY) => {
  const nextZoomClamped = clampZoom(nextZoom);
  if (nextZoomClamped === props.zoom) return;
  if (!canvasRef.value) {
    emit('update:zoom', nextZoomClamped);
    return;
  }
  const rect = canvasRef.value.getBoundingClientRect();
  const anchorX = clientX - rect.left;
  const anchorY = clientY - rect.top;
  const contentX = (anchorX - props.offsetX) / props.zoom;
  const contentY = (anchorY - props.offsetY) / props.zoom;
  emit('update:offsetX', anchorX - contentX * nextZoomClamped);
  emit('update:offsetY', anchorY - contentY * nextZoomClamped);
  emit('update:zoom', nextZoomClamped);
};

const zoomAtCenter = nextZoom => {
  if (!canvasRef.value) {
    emit('update:zoom', clampZoom(nextZoom));
    return;
  }
  const rect = canvasRef.value.getBoundingClientRect();
  zoomAtPoint(nextZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
};

const zoomIn = () => zoomAtCenter(props.zoom + ZOOM_STEP);
const zoomOut = () => zoomAtCenter(props.zoom - ZOOM_STEP);
const zoomReset = () => {
  emit('update:zoom', 1);
  emit('update:offsetX', 0);
  emit('update:offsetY', 0);
};

// Node cards size themselves to their content, so their extent is read back
// from the measured port offsets rather than assumed from a fixed card size.
const nodeExtent = node => {
  const ports = portOffsetsByNode[node.id];
  if (!ports) return { width: NODE_WIDTH, height: 120 };
  const xs = ports.out.map(p => p.x);
  const ys = [...ports.out, ...ports.in].map(p => p.y);
  return {
    width: Math.max(...xs, NODE_WIDTH) + 12,
    height: Math.max(...ys, 80) + 24,
  };
};

const fitToContent = () => {
  if (!canvasRef.value || !props.nodes.length) {
    zoomReset();
    return;
  }
  const rect = canvasRef.value.getBoundingClientRect();
  const PADDING = 48;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  props.nodes.forEach(node => {
    const { width, height } = nodeExtent(node);
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + width);
    maxY = Math.max(maxY, node.y + height);
  });
  const contentWidth = Math.max(maxX - minX, 1);
  const contentHeight = Math.max(maxY - minY, 1);
  const nextZoom = clampZoom(
    Math.min(
      (rect.width - PADDING * 2) / contentWidth,
      (rect.height - PADDING * 2) / contentHeight,
      1
    )
  );
  emit('update:zoom', nextZoom);
  emit(
    'update:offsetX',
    (rect.width - contentWidth * nextZoom) / 2 - minX * nextZoom
  );
  emit(
    'update:offsetY',
    (rect.height - contentHeight * nextZoom) / 2 - minY * nextZoom
  );
};

defineExpose({
  // Pans (without changing zoom) so a node sits in the middle of the view.
  centerOnNode: id => {
    const node = props.nodes.find(n => n.id === id);
    if (!node) return;
    const height = nodeHeights[id] || NODE_MIN_HEIGHT;
    emit(
      'update:offsetX',
      viewportWidth.value / 2 - (node.x + NODE_WIDTH / 2) * props.zoom
    );
    emit(
      'update:offsetY',
      viewportHeight.value / 2 - (node.y + height / 2) * props.zoom
    );
  },
  // Cancels a wire in progress; reports whether there was one.
  cancelConnecting: () => {
    if (!connecting.value) return false;
    clearConnecting();
    return true;
  },
  resetZoom: zoomReset,
  // Starts drawing a wire from an out-port, as if its dot had been clicked.
  startConnecting: (nodeId, portIdx) =>
    handlePortClick({ nodeId, side: 'out', portIdx }),
  fitToContent,
  getNodeHeights: () => ({ ...nodeHeights }),
  getPortOffsets: () => portOffsetsByNode,
  getViewportCenter: () => {
    const rect = canvasRef.value?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (rect.width / 2 - props.offsetX) / props.zoom,
      y: (rect.height / 2 - props.offsetY) / props.zoom,
    };
  },
});

const onWheel = event => {
  if (!canvasRef.value) return;
  if (event.ctrlKey || event.metaKey) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    zoomAtPoint(props.zoom + delta, event.clientX, event.clientY);
    return;
  }
  event.preventDefault();
  emit('update:offsetX', props.offsetX - event.deltaX);
  emit('update:offsetY', props.offsetY - event.deltaY);
};
</script>

<template>
  <section class="flex flex-col bg-n-alpha-1">
    <div
      ref="canvasRef"
      class="relative flex-1 overflow-hidden"
      :class="isPanMode ? 'cursor-grab' : 'cursor-default'"
      @dragover.prevent
      @drop="onDrop"
      @click="onCanvasBackgroundClick"
      @mousedown="onPanDown"
      @wheel="onWheel"
    >
      <div
        v-if="connecting"
        class="absolute z-20 px-3 py-1 text-xs font-medium -translate-x-1/2 border rounded-full pointer-events-none top-14 start-1/2 border-n-weak bg-n-solid-1 text-n-brand"
      >
        {{ t('AGENT_BOTS.VISUAL_BUILDER.CONNECT_HINT') }}
      </div>
      <div
        class="absolute z-20 bottom-3"
        :class="paletteCollapsed ? 'start-[72px]' : 'start-[264px]'"
      >
        <CsmlMinimap
          v-model:open="minimapOpen"
          :nodes="nodes"
          :heights="nodeHeights"
          :offset-x="offsetX"
          :offset-y="offsetY"
          :zoom="zoom"
          :viewport-width="viewportWidth"
          :viewport-height="viewportHeight"
          @pan="
            emit('update:offsetX', $event.x);
            emit('update:offsetY', $event.y);
          "
        />
      </div>
      <CsmlCanvasControls
        :zoom="zoom"
        :wire-style="wireStyle"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @zoom-to="zoomAtCenter"
        @fit="fitToContent"
        @tidy="emit('tidy')"
        @update:wire-style="emit('update:wireStyle', $event)"
      />
      <div
        v-if="marquee"
        class="absolute z-20 border rounded-sm pointer-events-none border-n-brand bg-n-brand/10"
        :style="{
          left: `${marquee.x}px`,
          top: `${marquee.y}px`,
          width: `${marquee.width}px`,
          height: `${marquee.height}px`,
        }"
      />
      <div
        v-if="!nodes.length"
        class="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
      >
        <p class="px-6 text-center text-sm text-n-slate-11">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.CANVAS_EMPTY') }}
        </p>
      </div>
      <div
        class="absolute inset-0 transition-opacity duration-150 pointer-events-none bg-[radial-gradient(circle,rgb(var(--slate-5))_1px,transparent_1px)]"
        :style="minorGridStyle"
      />
      <div
        class="absolute inset-0 pointer-events-none bg-[radial-gradient(circle,rgb(var(--slate-7))_1.5px,transparent_1.5px)]"
        :style="majorGridStyle"
      />
      <!-- Wires live on the viewport-sized SVG so they keep a real width/height;
           an inner group applies the same pan/zoom as the node world. -->
      <CsmlConnectionsLayer
        :nodes="nodes"
        :connections="connections"
        :selected-connection="selectedConnection"
        :temp-connection="tempConnection"
        :port-offsets="portOffsetsByNode"
        :offset-x="offsetX"
        :offset-y="offsetY"
        :zoom="zoom"
        :wire-style="wireStyle"
        @select-connection="emit('selectConnection', $event)"
        @remove-connection="emit('removeConnection', $event)"
      />
      <!-- The flow world is geometry, not prose: nodes sit at x/y offsets and
           wires leave on the right and arrive on the left. Pinning it to LTR
           keeps that true when the surrounding UI is mirrored for RTL. -->
      <div dir="ltr" class="absolute top-0 left-0 z-[2]" :style="transformStyle">
        <template v-if="guides">
          <span
            v-for="gx in guides.vertical"
            :key="`gv-${gx}`"
            class="absolute z-0 w-px bg-n-brand/60 -top-[5000px] h-[10000px] pointer-events-none"
            :style="{ left: `${gx}px` }"
          />
          <span
            v-for="gy in guides.horizontal"
            :key="`gh-${gy}`"
            class="absolute z-0 h-px bg-n-brand/60 -left-[5000px] w-[10000px] pointer-events-none"
            :style="{ top: `${gy}px` }"
          />
        </template>
        <CsmlFlowNode
          v-for="node in nodes"
          :key="node.id"
          :node="node"
          :selected="selectedNodeIds.includes(node.id)"
          :selection-size="selectedNodeIds.length"
          :zoom="zoom"
          :connecting-from-id="connectingFromId"
          :connected-ports="connectedPortsByNode[node.id] || []"
          :has-incoming="incomingNodeIds.has(node.id)"
          :is-pan-mode="isPanMode"
          @select="emit('selectNode', $event)"
          @drag-move="emit('nodeDrag', $event)"
          @drag-end="emit('nodeDragEnd')"
          @delete="emit('deleteNode', $event)"
          @duplicate="emit('duplicateNode', $event)"
          @add-next="emit('addNext', $event)"
          @add-at-port="emit('addAtPort', $event)"
          @port-click="handlePortClick"
          @ports-measured="onPortsMeasured"
        />
      </div>
    </div>
  </section>
</template>
